import {calculateHeight, drawCapacities} from "./draw-capacities";
import {FunctionalCapacityGroupDiagram} from "../../../../../services/back/functional-capacities.service";
import { drawOptionButton } from './draw-capacities';
import {drawPlusButton} from "./draw-capacities-child";
import {TranslateService} from "@ngx-translate/core";
import {updateAllAppImagesForScale} from "./draw-capacities-app";

interface StoredButton {
	id: string;
	name: string;
	type: string;
	x: number;
	y: number;
	width: number;
	height: number;
	hovered: boolean;
}

export class CapacityDiagramEngine {
	private offsetX = 0;
	private offsetY = 0;
	private pixelRatio: number;
	private canvasWidth: number;
	private canvasHeight: number;
	private hoveredButtonId: string | null = null;
	private hoveredButtonType: string | null = null;
	private storedButtons: StoredButton[] = [];
	private currentScale: number = 1;
	private angle: number = 0;

	constructor(
		protected ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
		protected canvas: HTMLCanvasElement,
		protected container: HTMLElement,
		protected registerButtonCallback: (id: string, name: string, type: string, x: number, y: number, width: number, height: number) => void,
		protected translate: TranslateService,
		protected data: FunctionalCapacityGroupDiagram[],
		protected gridSize: number,
		protected scale: number,
	) {
		this.pixelRatio = window.devicePixelRatio || 1;
		// this.pixelRatio = 1;
		this.currentScale = scale;
		this.initialize();
		this.data = data;
	}

	private initialize(): void {

		let margin: number = 0
		if( this.data.length < 4) {
			margin = (3*this.data.length*this.gridSize);
		} else if ( this.data.length < 6) {
			margin = (this.data.length*this.gridSize);
		} else {
			margin = (8*this.gridSize);
		}

		if (this.gridSize === 30) {
			this.angle = 8
		} else if (this.gridSize === 25) {
			this.angle = 7
		} else if (this.gridSize === 20) {
			this.angle = 6
		} else {
			this.angle = 8
		}

		//Calculate total width
		this.canvasWidth = this.data.length * (14 * this.gridSize) + margin;
		if (this.canvasWidth < this.container.clientWidth) {
			this.canvasWidth = 1.2 * this.container.clientWidth;
		}

		// Calculate total height
		this.canvasHeight = this.calculateGlobalHeight(this.data) + (14 * this.gridSize);

		this.ctx.imageSmoothingEnabled = true;
		this.ctx.imageSmoothingQuality = 'low';

		this.canvas.style.width = `${this.canvasWidth}px`;
		this.canvas.style.height = `${this.canvasHeight}px`;

		this.canvas.width = this.canvasWidth * this.pixelRatio;
		this.canvas.height = this.canvasHeight * this.pixelRatio;

		this.ctx.scale(this.pixelRatio, this.pixelRatio);
	}

	private calculateGlobalHeight(
		data: FunctionalCapacityGroupDiagram[]
	): number {

		let globalHeight: number = 0

		for (const element of data) {
			const height = calculateHeight(element.children, this.gridSize);

			if (height > globalHeight) {
				globalHeight = height
			}
		}
		if (globalHeight < 299) {
			globalHeight = 270
		}
		return globalHeight + (this.gridSize * 10);
	}

	public internalRegisterButton(id: string, name: string, type: string, x: number, y: number, width: number, height: number): void {
		this.registerButtonCallback(id, name, type, x, y, width, height);

		this.storedButtons.push({
			id,
			name,
			type,
			x,
			y,
			width,
			height,
			hovered: false
		})
	}

	public setHoveredButton(id: string | null, type: string | null): void {
		if (this.hoveredButtonId === id && this.hoveredButtonType === type) {
			return;
		}

		const oldHoveredButton = this.storedButtons.find(btn => btn.id === this.hoveredButtonId && btn.type === this.hoveredButtonType);
		const newHoveredButton = this.storedButtons.find(btn => btn.id === id && btn.type === type);

		this.hoveredButtonId = id;
		this.hoveredButtonType = type;

		// Mettre à jour les états de survol
		if (oldHoveredButton) {
			oldHoveredButton.hovered = false;
			this.redrawSingleButton(oldHoveredButton);
		}

		if (newHoveredButton) {
			newHoveredButton.hovered = true;
			this.redrawSingleButton(newHoveredButton);
		}
	}

	private redrawSingleButton(button: StoredButton): void {
		this.ctx.save();

		switch (button.type) {
			case 'child-capacity':
				drawPlusButton(this.ctx, button.x + this.gridSize, button.y + this.gridSize, button.hovered, this.gridSize);
				break
			case 'app-button':
				break
			case 'tool-tip-name':
				break
			case 'tool-tip-app-name':
				break
			case 'add-group':
				break
			case 'add-capacity':
				break
			default:
				drawOptionButton(this.ctx, button.x, button.y, button.width, button.hovered, button.type === 'group-option' ? 'violet' : 'rose');
				break;
		}
		this.ctx.restore();
	}

	public isButtonHovered(id: string): boolean {
		return this.hoveredButtonId === id;
	}

	public drawGrid(): void {
		const ctx = this.ctx;

		ctx.strokeStyle = 'rgba(220, 220, 233, 0.3)';
		ctx.lineWidth = 1;

		// Aligner sur des pixels entiers pour éviter le flou
		const alignToPixel = (value: number): number => Math.floor(value) + 0.5;

		// Dessiner les lignes verticales
		for (let x = -(this.gridSize - 20); x <= this.canvasWidth; x += this.gridSize) {
			const alignedX = alignToPixel(x);
			ctx.beginPath();
			ctx.moveTo(alignedX, 0);
			ctx.lineTo(alignedX, this.canvasHeight);
			ctx.stroke();
		}

		// Dessiner les lignes horizontales
		for (let y = -(this.gridSize - 20); y <= this.canvasHeight; y += this.gridSize) {
			const alignedY = alignToPixel(y);
			ctx.beginPath();
			ctx.moveTo(0, alignedY);
			ctx.lineTo(this.canvasWidth, alignedY);
			ctx.stroke();
		}
	}

	public drawElements(): void {

		const baseX: number = 20 + (this.gridSize * 2) + (this.data.length * this.gridSize);
		const baseY: number = 20 + (5 * this.gridSize);

		for (let i = 0; i < this.data.length; i++) {
			drawCapacities(
				this.ctx,
				baseX + (i * 12 * this.gridSize),
				baseY,
				this.data[i],
				this.gridSize,
				this.angle,
				this.internalRegisterButton.bind(this),
				this.isButtonHovered.bind(this),
				this.translate,
				this.currentScale,
			);
		}

		//Draw Add Group
		this.drawGeneralPlusButton(this.ctx, baseX, baseY, this.internalRegisterButton.bind(this), this.gridSize)
	}

	//Draw Plus button
	drawGeneralPlusButton(
		ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
		x: number,
		y: number,
		registerButton: (id: string, name: string, type: string, x: number, y: number, width: number, height: number) => void,
		gridSize: number,
	): void {

		const baseX: number = x - gridSize * 3;
		const baseY: number = y;

		ctx.beginPath();
		ctx.fillStyle = 'rgba(168, 144, 243, 1)';
		ctx.lineWidth = 1;
		ctx.arc(baseX + gridSize/2 + 0.25, baseY + gridSize/2 + 0.25, (gridSize/2), 0, 360);
		ctx.fill();
		ctx.beginPath();
		ctx.fillStyle = 'rgba(255, 255, 255, 1)';
		ctx.lineWidth = 1.5;
		ctx.lineCap = 'round';
		ctx.moveTo(baseX + gridSize / 2 + .5, baseY + gridSize / 3 + .5);
		ctx.lineTo(baseX + gridSize / 2 + .5, baseY + (gridSize / 3) * 2 + .5);
		ctx.moveTo(baseX + gridSize / 3 + .5, baseY + gridSize / 2 + .5);
		ctx.lineTo(baseX + (gridSize / 3) * 2 + .5, baseY + gridSize / 2 + .5);
		ctx.stroke();

		registerButton("02", "", "add-group", baseX, baseY, gridSize, gridSize);
	}

	public render(offsetX: number, offsetY: number): void {
		this.offsetX = offsetX;
		this.offsetY = offsetY;

		const ctx = this.ctx;

		// Effacer le canvas
		ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

		// Dessiner la grille
		this.drawGrid();

		// Dessiner les éléments
		this.drawElements();

		// Restaurer le contexte
		ctx.restore();
	}

	// Getter pour les dimensions
	public getDimensions(): { width: number, height: number } {
		return {
			width: this.canvas.width,
			height: this.canvas.height
		};
	}

	public updateOffset(offsetX: number, offsetY: number): void {
		this.offsetX = offsetX;
		this.offsetY = offsetY;
	}

	public async updateImageResolution(newScale: number): Promise<void> {
		await updateAllAppImagesForScale(this.ctx, newScale, this.angle);
	}
}
